/**
 * Lead Service - Orchestrates workflows, validation, and calls to repositories
 */

import * as leadRepository from "../repositories/leadRepository.js";
import api from "../../../shared/api.jsx";

/**
 * Validate lead status update data
 * @param {Object} updateData - Update data to validate
 * @returns {Object} Validation result
 */
const validateLeadStatusUpdate = (updateData) => {
  const { action, reason } = updateData;
  const errors = [];

  if (!action) {
    errors.push("Action is required");
  } else if (!["accept", "reject", "convert"].includes(action)) {
    errors.push("Action must be one of: accept, reject, convert");
  }

  if (action === "reject" && !reason) {
    errors.push("Reason is required when rejecting a lead");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validate lead info update data
 * @param {Object} updateData - Update data to validate
 * @returns {Object} Validation result
 */
const validateLeadInfoUpdate = (updateData) => {
  const { notes, contactInfo, category, phone } = updateData;
  const errors = [];

  if (notes && notes.length > 500) {
    errors.push("Notes must not exceed 500 characters");
  }

  if (contactInfo) {
    // if (contactInfo.phone && !/^\+27[0-9]{9}$/.test(contactInfo.phone)) {
    //   errors.push("Phone number must be in E.164 format (+27XXXXXXXXX)");
    // }
    if (
      contactInfo.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactInfo.email)
    ) {
      errors.push("Email must be a valid email address");
    }
  }

  if (
    category &&
    ![
      "moving",
      "delivery",
      "storage",
      "residential",
      "commercial",
      "industrial",
      "fulfilment_discovery",
    ].includes(category)
  ) {
    errors.push(
      "Category must be one of: moving, delivery, storage, residential, commercial, industrial, fulfilment discovery"
    );
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Get leads with filtering and pagination
 * @param {Object} filters - Filter criteria
 * @returns {Promise<Object>} Leads data with metadata
 */
export const getLeads = async (filters = {}) => {
  try {
    // Apply default filters
    const defaultFilters = {
      limit: 10,
      offset: 0,
      ...filters,
    };

    const leadsData = await leadRepository.fetchLeads(defaultFilters);

    // Handle both response formats (backend returns 'leads', frontend expects 'data')
    const leads = leadsData.leads || leadsData.data || leadsData;
    const count = leadsData.count || (Array.isArray(leads) ? leads.length : 0);

    return {
      success: true,
      data: leads,
      leads: leads, // Include both for compatibility
      count: count,
      filters: defaultFilters,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
      data: [],
      leads: [],
      count: 0,
    };
  }
};

/**
 * Get lead by ID with error handling
 * @param {string} leadId - Lead identifier
 * @returns {Promise<Object>} Lead data
 */
export const getLead = async (leadId) => {
  try {
    if (!leadId) {
      throw new Error("Lead ID is required");
    }

    const leadData = await leadRepository.fetchLead(leadId);

    if (leadData?.success === false) {
      return {
        success: false,
        error: leadData.error || "Failed to fetch lead",
        data: null,
      };
    }

    return {
      success: true,
      data: leadData?.data ?? leadData,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
      data: null,
    };
  }
};

/**
 * Get lead statistics
 * @returns {Promise<Object>} Lead statistics
 */
export const getLeadStats = async () => {
  try {
    const statsData = await leadRepository.fetchLeadStats();

    return {
      success: true,
      data: statsData.data || statsData,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
      data: null,
    };
  }
};

/**
 * Update lead status with validation and workflow
 * @param {string} leadId - Lead identifier
 * @param {Object} updateData - Update data
 * @returns {Promise<Object>} Update result
 */
export const updateLeadStatus = async (leadId, updateData) => {
  try {
    // Validate input
    const validation = validateLeadStatusUpdate(updateData);
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(", ")}`);
    }

    // Update lead status
    const result = await leadRepository.updateLeadStatus(leadId, updateData);

    return {
      success: true,
      data: result.data || result,
      message: `Lead ${updateData.action}ed successfully`,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Update lead information with validation
 * @param {string} leadId - Lead identifier
 * @param {Object} updateData - Update data
 * @returns {Promise<Object>} Update result
 */
export const updateLeadInfo = async (leadId, updateData) => {
  try {
    // Validate input
    const validation = validateLeadInfoUpdate(updateData);
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(", ")}`);
    }

    // Update lead info
    const result = await leadRepository.updateLead(leadId, updateData);

    return {
      success: true,
      data: result.data || result,
      message: "Lead information updated successfully",
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Bulk update lead statuses
 * @param {string[]} leadIds - Array of lead identifiers
 * @param {Object} actionData - Action data (action, reason, notes)
 * @returns {Promise<Object>} Bulk update result
 */
export const bulkUpdateLeadStatus = async (leadIds, actionData) => {
  try {
    if (!Array.isArray(leadIds) || leadIds.length === 0) {
      throw new Error("leadIds must be a non-empty array");
    }

    const response = await api.patch("/leads/update/bulk/status", {
      leadIds,
      ...actionData,
    });

    return {
      success: true,
      data: response.data || response,
      message: response.data?.message || response.message || "Bulk status update completed",
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || "Failed to bulk update lead statuses",
    };
  }
};

/**
 * Bulk update lead information
 * @param {string[]} leadIds - Array of lead identifiers
 * @param {Object} updateData - Update data (category, notes, moving_from, moving_to, etc.)
 * @returns {Promise<Object>} Bulk update result
 */
export const bulkUpdateLeadInfo = async (leadIds, updateData) => {
  try {
    if (!Array.isArray(leadIds) || leadIds.length === 0) {
      throw new Error("leadIds must be a non-empty array");
    }

    const response = await api.patch("/leads/update/bulk", {
      leadIds,
      ...updateData,
    });

    return {
      success: true,
      data: response.data || response,
      message: response.data?.message || response.message || "Bulk update completed",
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || "Failed to bulk update leads",
    };
  }
};

/**
 * Bulk soft delete leads
 * @param {string[]} leadIds - Array of lead identifiers
 * @param {string} reason - Deletion reason
 * @returns {Promise<Object>} Bulk delete result
 */
export const bulkDeleteLeads = async (leadIds, reason = "Bulk deletion") => {
  try {
    if (!Array.isArray(leadIds) || leadIds.length === 0) {
      throw new Error("leadIds must be a non-empty array");
    }

    const response = await api.patch("/leads/update/bulk/delete", {
      leadIds,
      reason,
    });

    return {
      success: true,
      data: response.data || response,
      message: response.data?.message || response.message || "Bulk delete completed",
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || "Failed to bulk delete leads",
    };
  }
};

export const convertLeadToQuote = async (
  leadId,
  leadData = null,
  conversionData = {}
) => {
  try {
    let lead = leadData;

    // Only fetch lead data if not provided
    if (!lead) {
      const leadResult = await getLead(leadId);
      if (!leadResult.success) {
        throw new Error(`Failed to get lead data: ${leadResult.error}`);
      }
      lead = leadResult.data;
    }

    // Prepare quote details from lead data
    // Support both legacy move_date and new move_dates array
    const datesArray = lead.move_dates && lead.move_dates.length > 0
      ? lead.move_dates.filter((d) => d && d.trim() !== "")
      : (lead.move_date ? [lead.move_date] : ["Not specified"]);

    const quoteDetails = {
      from: lead.moving_from || "Not specified",
      to: lead.moving_to || "Not specified",
      date: datesArray[0] || lead.move_date || "Not specified", // Keep for backward compatibility
      dates: datesArray, // New: array of dates
      items: lead.items || [],
      notes:
        conversionData.notes || lead.notes || `Converted from lead ${leadId}`,
    };

    // Call the backend conversion API using the api utility
    const result = await api.post("/conversion/lead-to-quote", {
      leadId,
      quoteDetails,
    });

    if (result.success) {
      return {
        success: true,
        data: result.data,
        message: "Lead successfully converted to quote",
        quoteId: result.quoteId,
      };
    } else {
      throw new Error(result.error || "Failed to convert lead to quote");
    }
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

export const convertLeadToOpportunity = async (leadId) => {
  try {
    const result = await api.post("/conversion/lead-to-opportunity", { leadId });
    if (result.success) {
      return {
        success: true,
        opportunityId: result.opportunityId,
        opportunityNumber: result.opportunityNumber,
        status: result.status,
        message:
          result.message || "Lead successfully converted to opportunity",
        existing: result.existing,
      };
    }
    throw new Error(result.error || "Failed to convert lead to opportunity");
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};
